import { useEffect, useState } from 'react';
import {
  Card, Title, Table, TableHead, TableRow, TableHeaderCell, TableBody, TableCell,
  Badge, Select, SelectItem, Button, TextInput, Dialog, DialogPanel, Flex, Text,
} from '@tremor/react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { adminApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import DashboardGrants from '../components/admin/DashboardGrants';
import GrantPicker, { Dashboard, Level } from '../components/admin/GrantPicker';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  enabled: boolean;
  invite_expired?: boolean;
  last_login?: string | null;
}

// "לפני 3 ימים" beats an ISO timestamp for the question this column answers:
// is this person alive in the system?
function relTime(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'היום';
  if (days === 1) return 'אתמול';
  if (days < 30) return `לפני ${days} ימים`;
  const months = Math.floor(days / 30);
  return months === 1 ? 'לפני חודש' : `לפני ${months} חודשים`;
}

// The role says WHERE a person may look; the per-dashboard grants (the "הרשאות"
// button on each row) say WHAT they see there.
//   admin      every dashboard, both environments, with edit
//   dev-view   their granted dashboards, on dev AND prod
//   prod-view  their granted dashboards, on prod only
// Every account was migrated to these three on 2026-07-26; the old names
// (viewer / editor / dev-viewer) can no longer be assigned.
const ROLES = ['admin', 'prod-view', 'dev-view'];

// What a new account starts as. The weakest role, deliberately: an account created
// before anyone decided what it is for should see the least, not the most. It also
// has to BE one of ROLES — it used to default to the retired 'viewer', which left the
// Select blank and made the server reject a form nobody had touched.
const DEFAULT_ROLE = 'prod-view';

const ROLE_HINT: Record<string, string> = {
  'admin': 'רואה ועורך הכל, ב-dev ובפרוד. אין מה לסמן למטה.',
  'prod-view': 'רואה את מה שסומן למטה — בפרוד בלבד.',
  'dev-view': 'רואה את מה שסומן למטה — גם ב-dev וגם בפרוד. זה מצב הפיילוט.',
};

export default function Admin() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: DEFAULT_ROLE });
  // Creating an account and deciding what it may see is ONE decision (Ohad 2026-07-26).
  // It used to be two — create, then remember to open "הרשאות" — and a user born
  // between the two steps could sign in and find nothing at all.
  const [newGrants, setNewGrants] = useState<Record<string, Level>>({});
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdInvite, setCreatedInvite] = useState<
    { email: string; granted: number; grantsError?: string | null } | null
  >(null);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendResult, setResendResult] = useState<{ email: string; action: string } | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [grantsFor, setGrantsFor] = useState<User | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // The access email — preview first, send on explicit confirm. The preview and
  // the send are composed by the same backend function, so what the admin reads
  // here is byte-for-byte what the user receives.
  const [emailFor, setEmailFor] = useState<User | null>(null);
  const [emailPreview, setEmailPreview] = useState<
    { to: string; subject: string; html: string; enabled: boolean } | null
  >(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  // Role edits waiting to be saved: {userId: newRole}. Empty = nothing pending.
  const [pendingRoles, setPendingRoles] = useState<Record<string, string>>({});
  const [savingRoles, setSavingRoles] = useState(false);
  const [saveResult, setSaveResult] = useState<{ id: string; line: string }[] | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const active = users.filter((u) => u.enabled !== false);
  const archived = users.filter((u) => u.enabled === false);
  const shown = tab === 'active' ? active : archived;

  // Disabling is the answer to "someone left" — it ends access now and keeps their
  // name attached to everything they approved. Deletion is for a real erasure request.
  // Staging changes creates a way to lose them; the browser's own guard is enough
  // to stop a pending edit from vanishing on a stray refresh.
  useEffect(() => {
    if (!Object.keys(pendingRoles).length) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingRoles]);

  const handleToggleEnabled = async (user: User) => {
    const next = user.enabled === false;
    // Said plainly, because it is the one part that does NOT come back with them:
    // re-enabling restores the account, not the dashboards.
    if (!next && !window.confirm(
      `להשבית את ${user.email}?\n\nהוא לא יוכל להתחבר יותר, וכל הרשאות הדשבורדים שלו יבוטלו מיד. התיעוד שלו נשמר, וניתן להפעיל בחזרה — אבל ההרשאות יצטרכו להינתן מחדש.`,
    )) return;
    setTogglingId(user.id);
    try {
      await adminApi.setUserEnabled(user.id, next);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, enabled: next } : u)));
    } finally {
      setTogglingId(null);
    }
  };

  const loadUsers = () => adminApi.listUsers().then((res) => setUsers(res.data));

  useEffect(() => {
    loadUsers();
    // The grantable dashboards come from the map on the server, so the create dialog
    // can never offer a dashboard that does not exist — or miss one that was added.
    adminApi.listDashboards().then((res) => setDashboards(res.data)).catch(() => {});
  }, []);

  // A role change is staged, not sent (Ohad 2026-07-28). It used to fire on the
  // dropdown itself, so the one action on this screen with real consequences gave
  // no sign it had happened — and no chance to take it back. Now the row marks
  // itself, a bar appears, and saving says out loud what changed.
  const stageRole = (userId: string, role: string) => {
    setSaveResult(null);
    setPendingRoles((prev) => {
      const next = { ...prev };
      const original = users.find((u) => u.id === userId)?.role;
      if (role === original) delete next[userId];
      else next[userId] = role;
      return next;
    });
  };

  const pendingCount = Object.keys(pendingRoles).length;

  const saveRoles = async () => {
    setSavingRoles(true);
    setSaveError(null);
    const entries = Object.entries(pendingRoles);
    try {
      for (const [userId, role] of entries) {
        await adminApi.setUserRole(userId, role);
      }
      setUsers((prev) => prev.map((u) => (pendingRoles[u.id] ? { ...u, role: pendingRoles[u.id] } : u)));
      // Kept as {id, line} so the success box can offer "email them about it" —
      // the changed accounts are exactly the ones worth telling.
      setSaveResult(entries.map(([id, role]) => ({
        id,
        line: `${users.find((u) => u.id === id)?.email || id} → ${role}`,
      })));
      setPendingRoles({});
    } catch (e: any) {
      setSaveError(e?.response?.data?.detail || 'השמירה נכשלה — אף שינוי לא נשמר בוודאות, רענן ובדוק');
    } finally {
      setSavingRoles(false);
    }
  };

  const openCreate = () => {
    setForm({ email: '', name: '', role: DEFAULT_ROLE });
    setNewGrants({});
    setError(null);
    setCreateOpen(true);
  };

  const pickNewGrant = (dashboardId: string, level: Level) => {
    setNewGrants((prev) => {
      const next = { ...prev };
      if (level === 'none') delete next[dashboardId];
      else next[dashboardId] = level;
      return next;
    });
  };

  const handleResend = async (user: User) => {
    setResendingId(user.id);
    setResendError(null);
    try {
      const { data } = await adminApi.resendInvitation(user.id);
      setResendResult({ email: data.email, action: data.action });
    } catch (e: any) {
      setResendError(e?.response?.data?.detail || 'Failed to resend');
    } finally {
      setResendingId(null);
    }
  };

  const openEmail = (user: User) => {
    setEmailFor(user);
    setEmailPreview(null);
    setEmailError(null);
    setEmailSentTo(null);
    setEmailLoading(true);
    adminApi.previewAccessEmail(user.id)
      .then((res) => setEmailPreview(res.data))
      .catch((e: any) => setEmailError(e?.response?.data?.detail || 'טעינת התצוגה נכשלה'))
      .finally(() => setEmailLoading(false));
  };

  const submitSendEmail = async () => {
    if (!emailFor) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      const { data } = await adminApi.sendAccessEmail(emailFor.id);
      setEmailSentTo(data.to);
    } catch (e: any) {
      setEmailError(e?.response?.data?.detail || 'השליחה נכשלה');
    } finally {
      setEmailSending(false);
    }
  };

  const submitDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await adminApi.deleteUser(confirmDelete.id);
      setConfirmDelete(null);
      await loadUsers();
    } catch (e: any) {
      setDeleteError(e?.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const submitCreate = async () => {
    if (!form.email) { setError('Email is required'); return; }
    // An admin holds everything by definition, so ticking boxes for one would write
    // grants that decide nothing. Anyone else with no ticks would sign in to a wall,
    // which is legal but almost never what was meant — so it is confirmed, not blocked.
    const grants = form.role === 'admin' ? {} : newGrants;
    if (form.role !== 'admin' && !Object.keys(grants).length && !window.confirm(
      'לא סימנת אף דשבורד. המשתמש ייווצר ולא יראה כלום עד שתיתן לו הרשאות. להמשיך?',
    )) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await adminApi.createUser({ ...form, grants });
      setCreateOpen(false);
      setCreatedInvite({
        email: data.email,
        granted: Object.keys(data.grants || {}).length,
        grantsError: data.grants_error,
      });
      await loadUsers();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="User Management" />
        <main className="flex-1 overflow-y-auto p-6">
          <Card>
            <Flex justifyContent="between" alignItems="center">
              <Title>{tab === 'active' ? 'Users' : 'ארכיון'}</Title>
              <Flex justifyContent="end" className="gap-2 w-auto">
                {/* Two views of one state: an account is either enabled or it isn't.
                    A separate "archived" flag on top of that would only invent
                    combinations nobody could reason about. */}
                <Button
                  size="xs"
                  variant={tab === 'active' ? 'primary' : 'secondary'}
                  onClick={() => setTab('active')}
                >
                  פעילים ({active.length})
                </Button>
                <Button
                  size="xs"
                  variant={tab === 'archived' ? 'primary' : 'secondary'}
                  onClick={() => setTab('archived')}
                >
                  ארכיון ({archived.length})
                </Button>
                {tab === 'active' && <Button onClick={openCreate}>Create user</Button>}
              </Flex>
            </Flex>
            {tab === 'archived' && (
              <Text className="mt-3 text-gray-500">
                עובדים שהושבתו. הם אינם יכולים להתחבר, הרשאות הדשבורדים שלהם בוטלו
                בעת ההשבתה, והתיעוד שלהם — אישורי תשלומים, שינויי הרשאות — נשאר על שמם.
                אפשר להחזיר אותם (ואז לתת הרשאות מחדש), או למחוק לצמיתות מכאן.
              </Text>
            )}
            {/* Nothing on this screen is committed silently any more: while a role
                edit is pending the bar states what will change, and after saving it
                names every account that moved — plus the sign-out rule, which is the
                part that makes a correct change look broken. */}
            {pendingCount > 0 && (
              <Flex justifyContent="between" alignItems="center"
                className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                <Text className="text-amber-900 font-medium">
                  {pendingCount === 1 ? 'שינוי אחד לא נשמר' : `${pendingCount} שינויים לא נשמרו`}
                </Text>
                <Flex className="gap-2 w-auto">
                  <Button size="xs" variant="secondary" disabled={savingRoles}
                    onClick={() => { setPendingRoles({}); setSaveError(null); }}>
                    בטל
                  </Button>
                  <Button size="xs" loading={savingRoles} onClick={saveRoles}>
                    שמור שינויים
                  </Button>
                </Flex>
              </Flex>
            )}
            {saveError && (
              <Text className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-700">
                {saveError}
              </Text>
            )}
            {saveResult && (
              <div className="mt-4 rounded-lg border border-green-300 bg-green-50 px-4 py-3">
                <Flex justifyContent="between" alignItems="start">
                  <div>
                    <Text className="text-green-900 font-medium">
                      ✓ נשמר · {saveResult.length === 1 ? 'שינוי אחד' : `${saveResult.length} שינויים`}
                    </Text>
                    {saveResult.map(({ id, line }) => (
                      <Flex key={id} justifyContent="start" alignItems="center" className="gap-2 mt-0.5 w-auto">
                        <Text className="text-xs text-green-800">{line}</Text>
                        {/* The saved state is exactly what the access email describes —
                            this is the moment sending it makes sense. Still a manual act. */}
                        <Button size="xs" variant="light" onClick={() => {
                          const u = users.find((x) => x.id === id);
                          if (u) openEmail(u);
                        }}>
                          שלח מייל עדכון
                        </Button>
                      </Flex>
                    ))}
                    <Text className="text-xs text-green-800 mt-2">
                      ⚠ שינוי role נכנס לתוקף רק אחרי שהמשתמש יתנתק ויתחבר מחדש — רענון לא מספיק.
                    </Text>
                  </div>
                  <Button size="xs" variant="light" onClick={() => setSaveResult(null)}>סגור</Button>
                </Flex>
              </div>
            )}
            <Table className="mt-4">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Email</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Role</TableHeaderCell>
                  <TableHeaderCell>דשבורדים</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>פעילות אחרונה</TableHeaderCell>
                  <TableHeaderCell></TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((user) => (
                  <TableRow key={user.id} className={pendingRoles[user.id] ? 'bg-amber-50' : ''}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.name || <Text className="text-gray-500">—</Text>}</TableCell>
                    <TableCell>
                      <Select value={pendingRoles[user.id] || user.role}
                        onValueChange={(v) => stageRole(user.id, v)}>
                        {/* an unmigrated role stays listed only while its holder still has it */}
                        {[...ROLES, ...(ROLES.includes(user.role) ? [] : [user.role])]
                          .map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </Select>
                      {pendingRoles[user.id] && (
                        <Text className="mt-1 text-xs text-amber-700">
                          {user.role} → {pendingRoles[user.id]} · לא נשמר
                        </Text>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => setGrantsFor(user)}
                        title="בחר אילו דשבורדים המשתמש רואה, ואיפה הוא רשאי גם לערוך"
                      >
                        {user.role === 'admin' ? 'הכל' : 'הרשאות'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {user.enabled === false
                        ? <Badge color="red" tooltip="החשבון מושבת — לא יכול להתחבר">מושבת</Badge>
                        : user.invite_expired
                          ? <Badge color="orange" tooltip="הסיסמה הזמנית שנשלחה בהזמנה כבר לא בתוקף — לחץ 'שלח הזמנה' כדי לשלוח חדשה">הזמנה פגה</Badge>
                          : <Badge color={user.status === 'confirmed' ? 'green' : 'gray'}>{user.status}</Badge>}
                    </TableCell>
                    <TableCell>
                      {user.last_login
                        ? <span title={user.last_login}>{relTime(user.last_login)}</span>
                        : <Text className="text-red-500 text-sm">מעולם לא</Text>}
                    </TableCell>
                    <TableCell>
                      <Flex justifyContent="end" className="gap-2">
                        {tab === 'active' && (
                          <Button
                            size="xs"
                            variant="secondary"
                            onClick={() => openEmail(user)}
                            title="מייל אישי ממך עם לינק לסביבה הנכונה ופירוט ההרשאות — עם תצוגה מקדימה לפני שליחה"
                          >
                            שלח לינק
                          </Button>
                        )}
                        {tab === 'active' && (
                          <Button
                            size="xs"
                            variant="secondary"
                            onClick={() => handleResend(user)}
                            loading={resendingId === user.id}
                            title={user.status === 'force_change_password' ? 'שלח שוב מייל הזמנה' : 'שלח קישור איפוס סיסמה'}
                          >
                            {user.status === 'force_change_password' ? 'שלח הזמנה' : 'אפס סיסמה'}
                          </Button>
                        )}
                        {/* Direct delete for an account that NEVER logged in (expired /
                            unclaimed invitation) — there is no payslip record to protect,
                            so the archive-first safety doesn't apply. Lets an admin delete
                            and recreate to send a fresh invitation. Ohad 2026-08-10. */}
                        {tab === 'active' && !user.last_login && user.id !== currentUser?.sub && (
                          <Button
                            size="xs"
                            variant="secondary"
                            color="red"
                            onClick={() => { setDeleteError(null); setConfirmDelete(user); }}
                            title="מחיקה — זמין למי שמעולם לא נכנס. מחק והקם מחדש כדי לשלוח הזמנה טרייה"
                          >
                            מחק
                          </Button>
                        )}
                        {user.id !== currentUser?.sub && (
                          <Button
                            size="xs"
                            variant="secondary"
                            loading={togglingId === user.id}
                            onClick={() => handleToggleEnabled(user)}
                            title={user.enabled === false
                              ? 'החזר גישה למשתמש'
                              : 'סיים את הגישה שלו מיד, בלי למחוק את התיעוד שלו'}
                          >
                            {user.enabled === false ? 'הפעל' : 'השבת'}
                          </Button>
                        )}
                        {/* Deletion lives in the archive only. Ending someone's access
                            and erasing their record are different decisions, and the
                            reversible one has to come first — these accounts are the
                            names on approved payslips. */}
                        {tab === 'archived' && user.id !== currentUser?.sub && (
                          <Button
                            size="xs"
                            variant="secondary"
                            color="red"
                            onClick={() => { setDeleteError(null); setConfirmDelete(user); }}
                            title="מחיקה לצמיתות — התיעוד שלו יישאר בלי שם מאחוריו"
                          >
                            מחק לצמיתות
                          </Button>
                        )}
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </main>
      </div>

      {/* One dialog, both axes: the role says WHERE this person may look, the ticks
          below say WHAT. They are written together — see submitCreate. */}
      <Dialog open={createOpen} onClose={() => !submitting && setCreateOpen(false)} static>
        <DialogPanel className="max-w-xl max-h-[90vh] overflow-y-auto">
          <Title>משתמש חדש</Title>
          <div className="mt-4 space-y-4">
            <div>
              <Text>Email</Text>
              <TextInput
                placeholder="user@taliazhealth.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <Text>שם (לא חובה)</Text>
              <TextInput
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Text>סביבה (Role)</Text>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </Select>
              <Text className="mt-1 text-xs text-gray-500">{ROLE_HINT[form.role]}</Text>
            </div>
            <div dir="rtl">
              <Text>דשבורדים</Text>
              {form.role === 'admin' ? (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  משתמש אדמין — רואה ועורך את הכל. אין מה לסמן.
                </div>
              ) : dashboards.length ? (
                <div className="mt-2">
                  <GrantPicker dashboards={dashboards} value={newGrants} onPick={pickNewGrant} />
                  <Text className="mt-2 text-xs text-gray-500">
                    {Object.keys(newGrants).length
                      ? `${Object.keys(newGrants).length} דשבורדים ייפתחו לו עם היצירה`
                      : 'לא סומן כלום — המשתמש ייווצר בלי גישה לשום דשבורד'}
                  </Text>
                </div>
              ) : (
                <Text className="mt-2 text-xs text-gray-500">טוען דשבורדים…</Text>
              )}
            </div>
            {error && <Text color="red">{error}</Text>}
          </div>
          <Flex justifyContent="end" className="mt-6 gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={submitting}>ביטול</Button>
            <Button onClick={submitCreate} loading={submitting}>צור משתמש</Button>
          </Flex>
        </DialogPanel>
      </Dialog>

      {/* The access email. Preview IS the contract: the same backend composer
          renders this and the actual send, so approving here approves exactly
          what lands in their inbox. */}
      <Dialog open={!!emailFor} onClose={() => !emailSending && setEmailFor(null)} static>
        <DialogPanel className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <Title>מייל גישה — {emailFor?.email}</Title>
          {emailLoading && <Text className="mt-4">טוען תצוגה מקדימה…</Text>}
          {emailPreview && (
            <div className="mt-4 space-y-3">
              <Text className="text-xs text-gray-500">
                אל: {emailPreview.to} · נושא: {emailPreview.subject}
              </Text>
              {emailFor && pendingRoles[emailFor.id] && (
                <Text className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  שים לב: יש ל-role של המשתמש שינוי שלא נשמר ({emailFor.role} ← {pendingRoles[emailFor.id]}).
                  המייל משקף את המצב השמור — שמור קודם אם התכוונת לשלוח את החדש.
                </Text>
              )}
              <div
                className="rounded-lg border border-gray-200 bg-white px-4 py-3"
                dangerouslySetInnerHTML={{ __html: emailPreview.html }}
              />
              {!emailPreview.enabled && (
                <Text className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  שליחת מיילים עוד לא הופעלה (חסרה סיסמת האפליקציה של Google) — בינתיים תצוגה מקדימה בלבד.
                </Text>
              )}
            </div>
          )}
          {emailSentTo && (
            <Text className="mt-3 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-green-800">
              ✓ נשלח אל {emailSentTo}
            </Text>
          )}
          {emailError && <Text color="red" className="mt-3">{emailError}</Text>}
          <Flex justifyContent="end" className="mt-6 gap-2">
            <Button variant="secondary" onClick={() => setEmailFor(null)} disabled={emailSending}>
              סגור
            </Button>
            {!emailSentTo && (
              <Button
                onClick={submitSendEmail}
                loading={emailSending}
                disabled={!emailPreview || !emailPreview.enabled}
                title={emailPreview && !emailPreview.enabled ? 'חסרה סיסמת אפליקציה — ראה הודעה למעלה' : undefined}
              >
                שלח מייל
              </Button>
            )}
          </Flex>
        </DialogPanel>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => !deleting && setConfirmDelete(null)} static>
        <DialogPanel>
          <Title>Delete user</Title>
          {confirmDelete && (
            <Text className="mt-2">
              Are you sure you want to delete <span className="font-mono">{confirmDelete.email}</span>? This action cannot be undone.
            </Text>
          )}
          {deleteError && <Text color="red" className="mt-2">{deleteError}</Text>}
          <Flex justifyContent="end" className="mt-6 gap-2">
            <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
            <Button color="red" onClick={submitDelete} loading={deleting}>Delete</Button>
          </Flex>
        </DialogPanel>
      </Dialog>

      <Dialog open={!!resendResult || !!resendError} onClose={() => { setResendResult(null); setResendError(null); }} static>
        <DialogPanel>
          <Title>{resendError ? 'שגיאה' : 'נשלח'}</Title>
          {resendResult && (
            <Text className="mt-2">
              {resendResult.action === 'invitation_resent'
                ? <>נשלח מייל הזמנה מחדש לכתובת <span className="font-mono">{resendResult.email}</span> עם סיסמה זמנית חדשה.</>
                : <>נשלח קישור איפוס סיסמה לכתובת <span className="font-mono">{resendResult.email}</span>.</>
              }
              <br /><br />
              <span className="text-gray-500 text-xs">⚠ ייתכן שיגיע ל-Spam — שווה לתת ידיעה.</span>
            </Text>
          )}
          {resendError && <Text color="red" className="mt-2">{resendError}</Text>}
          <Flex justifyContent="end" className="mt-6">
            <Button onClick={() => { setResendResult(null); setResendError(null); }}>סגור</Button>
          </Flex>
        </DialogPanel>
      </Dialog>

      <Dialog open={!!createdInvite} onClose={() => setCreatedInvite(null)} static>
        <DialogPanel>
          <Title>{createdInvite?.grantsError ? 'המשתמש נוצר — ההרשאות לא' : 'ההזמנה נשלחה'}</Title>
          {createdInvite && (
            <Text className="mt-2">
              נשלח מייל הזמנה עם סיסמה זמנית ל-{' '}
              <span className="font-mono">{createdInvite.email}</span>, והוא יתבקש לבחור סיסמה בכניסה הראשונה.
              {!createdInvite.grantsError && (
                <> {createdInvite.granted
                  ? `נפתחו לו ${createdInvite.granted} דשבורדים.`
                  : 'לא ניתנה לו גישה לאף דשבורד — אפשר לתת דרך "הרשאות" ברשימה.'}</>
              )}
            </Text>
          )}
          {/* The account is created first and the grants written after, so this is the
              one state where the two can disagree. Said out loud rather than swallowed:
              the person can sign in and would find nothing. */}
          {createdInvite?.grantsError && (
            <Text color="red" className="mt-2">
              החשבון נוצר, אבל שמירת ההרשאות נכשלה — הוא לא יראה כלום. תן לו אותן דרך
              "הרשאות" ברשימת המשתמשים. ({createdInvite.grantsError})
            </Text>
          )}
          <Flex justifyContent="end" className="mt-6">
            <Button onClick={() => setCreatedInvite(null)}>סגור</Button>
          </Flex>
        </DialogPanel>
      </Dialog>

      {grantsFor && (
        <DashboardGrants
          email={grantsFor.email}
          role={grantsFor.role}
          disabled={grantsFor.enabled === false}
          onClose={() => setGrantsFor(null)}
        />
      )}
    </div>
  );
}
